import React, { useState, useEffect } from 'react';
import { Card, CardInput } from '../types/models';
import { useAppContext } from '../context/RevisionContext';
import CardEditor from './CardEditor';
import './CardList.css';

interface CardListProps {
  sectionId: string;
  onCardsChange: () => Promise<void>;
}

/**
 * CardList subcomponent
 * Displays all cards within a section with edit/delete operations
 * Requirements: 1.5
 */
const CardList: React.FC<CardListProps> = ({ sectionId, onCardsChange }) => {
  const { user, config, firestoreService } = useAppContext();
  
  // State
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor state
  const [showEditor, setShowEditor] = useState(false);
  const [editingCard, setEditingCard] = useState<Card | undefined>(undefined);

  // Delete confirmation state
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);

  /**
   * Load cards for the section
   */
  useEffect(() => {
    loadCards();
  }, [sectionId]);

  /**
   * Load cards from Firestore
   */
  const loadCards = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const loadedCards = await firestoreService.getCardsBySection(sectionId);
      setCards(loadedCards);
    } catch (err) {
      console.error('Error loading cards:', err);
      setError('Failed to load cards. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle create card button
   */
  const handleCreateCard = () => {
    setEditingCard(undefined);
    setShowEditor(true);
  };

  /**
   * Handle edit card button
   */
  const handleEditCard = (card: Card) => {
    setEditingCard(card);
    setShowEditor(true);
  };

  /**
   * Handle save card (create or update)
   */
  const handleSaveCard = async (cardInput: CardInput) => {
    try {
      if (editingCard) {
        // Update existing card
        await firestoreService.updateCard(editingCard.id, cardInput);
      } else {
        // Create new card
        await firestoreService.createCard(cardInput);
      }

      // Close editor and refresh
      setShowEditor(false);
      setEditingCard(undefined);
      await loadCards();
      await onCardsChange(); // Refresh section card count
    } catch (err) {
      console.error('Error saving card:', err);
      throw err; // Let CardEditor handle the error display
    }
  };

  /**
   * Handle cancel editor
   */
  const handleCancelEditor = () => {
    setShowEditor(false);
    setEditingCard(undefined);
  };

  /**
   * Handle delete card button
   */
  const handleDeleteCard = (cardId: string) => {
    setDeletingCardId(cardId);
  };

  /**
   * Confirm delete card
   */
  const confirmDeleteCard = async () => {
    if (!deletingCardId) return;

    try {
      await firestoreService.deleteCard(deletingCardId);
      setDeletingCardId(null);
      await loadCards();
      await onCardsChange(); // Refresh section card count
    } catch (err) {
      console.error('Error deleting card:', err);
      alert('Failed to delete card. Please try again.');
    }
  };

  /**
   * Cancel delete
   */
  const cancelDelete = () => {
    setDeletingCardId(null);
  };

  /**
   * Truncate text for preview
   */
  const truncateText = (text: string, maxLength: number = 100): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (isLoading) {
    return <div className="card-list-loading">Loading cards...</div>;
  }

  if (error) {
    return (
      <div className="card-list-error">
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadCards}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="card-list">
      <div className="card-list-header">
        <h3>Cards</h3>
        <button className="btn btn-primary" onClick={handleCreateCard}>
          Create Card
        </button>
      </div>

      {cards.length === 0 ? (
        <div className="empty-message">
          <p>No cards in this section yet.</p>
          <button className="btn btn-primary" onClick={handleCreateCard}>
            Create Your First Card
          </button>
        </div>
      ) : (
        <ul className="cards">
          {cards.map((card) => (
            <li key={card.id} className="card-item">
              <div className="card-preview">
                <div className="card-front">
                  <span className="card-label">Front:</span>
                  <span className="card-text">
                    {truncateText(card.front.plainText)}
                  </span>
                </div>
                {card.tags.length > 0 && (
                  <div className="card-tags">
                    {card.tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="card-actions">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleEditCard(card)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteCard(card.id)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Card Editor Modal */}
      {showEditor && (
        <div className="modal-overlay">
          <div className="modal card-editor-modal">
            <CardEditor
              config={config}
              card={editingCard}
              sectionId={sectionId}
              onSave={handleSaveCard}
              onCancel={handleCancelEditor}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deletingCardId && (
        <div className="modal-overlay">
          <div className="modal delete-confirmation">
            <h3>Delete Card</h3>
            <p>Are you sure you want to delete this card? This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn btn-danger" onClick={confirmDeleteCard}>
                Delete
              </button>
              <button className="btn btn-secondary" onClick={cancelDelete}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardList;
